"use client";

import type { CSSProperties, ReactNode, Ref } from "react";
import { useCallback } from "react";
import { cn } from "../../lib";
import { PanZoomScaleProvider } from "./pan-zoom-scale-context";
import {
	type SetPanZoomTargetOptions,
	type UsePanZoomSurfaceOptions,
	usePanZoomSurface,
} from "./use-pan-zoom-surface";

export interface PanZoomSurfaceRenderContext {
	zoom: number;
	scale: number;
	zoomStep: number;
	canPanX: boolean;
	canPanY: boolean;
	viewportWidth: number;
	viewportHeight: number;
	setZoom: (targetZoom: number, options?: SetPanZoomTargetOptions) => void;
	zoomIn: () => void;
	zoomOut: () => void;
	/** Pan so a content-space point sits at the viewport center (play follow). */
	panToCenterContentPoint: (
		contentPoint: { x: number; y: number },
		options?: { animate?: boolean },
	) => void;
	/** Pan + zoom in one animated update (toolbar Reset). */
	panAndZoomToCenterContentPoint: (
		contentPoint: { x: number; y: number },
		targetZoom: number,
		options?: { animate?: boolean },
	) => void;
	getContentPointAtViewportCenter: () => { x: number; y: number };
	getPan: () => { x: number; y: number };
	setPanImmediate: (
		next: { x: number; y: number },
		options?: { animate?: boolean },
	) => void;
	getViewportElement: () => HTMLElement | null;
}

export interface PanZoomSurfaceProps extends UsePanZoomSurfaceOptions {
	className?: string;
	contentClassName?: string;
	contentStyle?: CSSProperties;
	contentRef?: Ref<HTMLDivElement>;
	contentPointerEvents?: "auto" | "none";
	renderContent: (context: PanZoomSurfaceRenderContext) => ReactNode;
	renderOverlay?: (context: PanZoomSurfaceRenderContext) => ReactNode;
	tabIndex?: number;
}

export function PanZoomSurface({
	className,
	contentClassName,
	contentStyle,
	contentRef,
	contentPointerEvents = "auto",
	renderContent,
	renderOverlay,
	tabIndex = 0,
	...options
}: PanZoomSurfaceProps) {
	const panZoom = usePanZoomSurface(options);

	const mergedContentRef = useCallback(
		(node: HTMLDivElement | null) => {
			panZoom.setContentRef(node);
			if (typeof contentRef === "function") {
				contentRef(node);
				return;
			}
			if (contentRef) {
				contentRef.current = node;
			}
		},
		[contentRef, panZoom],
	);

	const context: PanZoomSurfaceRenderContext = {
		zoom: panZoom.zoom,
		scale: panZoom.scale,
		zoomStep: panZoom.zoomStep,
		canPanX: panZoom.canPanX,
		canPanY: panZoom.canPanY,
		viewportWidth: panZoom.viewportSize.width,
		viewportHeight: panZoom.viewportSize.height,
		setZoom: panZoom.setZoom,
		zoomIn: panZoom.zoomIn,
		zoomOut: panZoom.zoomOut,
		panToCenterContentPoint: panZoom.panToCenterContentPoint,
		panAndZoomToCenterContentPoint: panZoom.panAndZoomToCenterContentPoint,
		getContentPointAtViewportCenter: panZoom.getContentPointAtViewportCenter,
		getPan: panZoom.getPan,
		setPanImmediate: panZoom.setPanImmediate,
		getViewportElement: panZoom.getViewportElement,
	};

	return (
		<div
			ref={panZoom.setViewportRef}
			className={cn(
				"relative min-h-0 flex-1 overflow-hidden overscroll-contain touch-none outline-none",
				className,
			)}
			tabIndex={tabIndex}
		>
			<PanZoomScaleProvider
				zoom={panZoom.zoom}
				scale={panZoom.scale}
				pan={panZoom.pan}
				viewportWidth={panZoom.viewportSize.width}
				viewportHeight={panZoom.viewportSize.height}
				transformMode={options.transformMode ?? "css"}
				animateTransform={panZoom.animateTransform}
				transitionDurationMs={panZoom.transitionDurationMs}
			>
				<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
					<div
						ref={mergedContentRef}
						className={cn("relative shrink-0", contentClassName)}
						style={{
							...contentStyle,
							...panZoom.contentStyle,
							pointerEvents: contentPointerEvents,
						}}
					>
						{renderContent(context)}
					</div>
				</div>
				{renderOverlay?.(context)}
			</PanZoomScaleProvider>
		</div>
	);
}
